/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
import React, { useState, useRef } from 'react';
import { useAppStore } from '../../store/useAppStore';
import './ExportImport.css';

export const ExportImportModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [message, setMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const exportDataComplete = useAppStore(state => state.exportDataComplete);
  const importDataFile = useAppStore(state => state.importDataFile);
  const resetData = useAppStore(state => state.resetData);
  const coau = useAppStore(state => state.coau);

  const handleExport = async () => {
    setIsExporting(true);
    setMessage('');
    
    try {
      await exportDataComplete();
      setMessage('✅ Export completato! File scaricato.');
      
      setTimeout(() => {
        setMessage('');
      }, 3000);
    } catch (error) {
      console.error('Errore export:', error);
      setMessage('❌ Errore durante l\'export');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.dos518')) {
      setMessage('❌ Formato file non valido. Usa file .dos518');
      return;
    }

    setIsImporting(true);
    setMessage('📥 Importazione in corso...');

    try {
      await importDataFile(file);
      setMessage('✅ Import completato! Dati caricati.');
      
      setTimeout(() => {
        setMessage('');
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Errore import:', error);
      setMessage('❌ Errore durante l\'import');
    } finally {
      setIsImporting(false);
    }
  };

  const handleReset = () => {
    if (confirm('⚠️ ATTENZIONE!\n\nQuesto cancellerà TUTTI i dati dell\'operazione corrente:\n- COAU\n- Coordinate\n- Risorse\n- Media\n- SITAC\n- LACES\n- Fraseologia\n\nSei sicuro di voler procedere?')) {
      resetData();
      setMessage('✅ Dati resettati');
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  };

  return (
    <div className="modal-overlay-export" onClick={onClose}>
      <div className="modal-content-export" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-export">
          <h2>🔄 Passaggio Consegna DOS</h2>
          <button className="modal-close-export" onClick={onClose}>×</button>
        </div>

        <div className="modal-body-export">
          
          {/* Info Operazione Corrente */}
          <div className="current-operation-info">
            <h3>📋 Operazione Corrente</h3>
            {coau?.numeroCoau ? (
              <div className="operation-details">
                <p><strong>Numero COAU:</strong> {coau.numeroCoau}</p>
                <p><strong>Località:</strong> {coau.localita || 'N/A'}</p>
                <p><strong>Comune:</strong> {coau.comune || 'N/A'}</p>
                <p><strong>DOS:</strong> {coau.dos || 'N/A'}</p>
              </div>
            ) : (
              <p className="no-operation">Nessuna operazione in corso</p>
            )}
          </div>

          {/* Sezione Export */}
          <div className="section-export">
            <h3>📤 Esporta Operazione</h3>
            <p className="section-description">
              Genera file .dos518 con tutti i dati dell'operazione (COAU, Coordinate, Risorse, Media, SITAC, LACES, Fraseologia)
            </p>
            <button 
              className="btn-action btn-export"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <span className="spinner">⏳</span> Generazione in corso...
                </>
              ) : (
                <>
                  📤 Esporta Consegna Completa
                </>
              )}
            </button>
          </div>

          {/* Sezione Import */}
          <div className="section-import">
            <h3>📥 Importa Operazione</h3>
            <p className="section-description">
              Carica file .dos518 da operazione precedente per continuare l'intervento
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".dos518"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button 
              className="btn-action btn-import"
              onClick={handleImport}
              disabled={isImporting}
            >
              {isImporting ? (
                <>
                  <span className="spinner">⏳</span> Importazione in corso...
                </>
              ) : (
                <>
                  📥 Importa Consegna
                </>
              )}
            </button>
          </div>

          {/* Sezione Reset */}
          <div className="section-reset">
            <h3>🗑️ Reset Dati</h3>
            <p className="section-description warning">
              ⚠️ Cancella TUTTI i dati dell'operazione corrente. Operazione irreversibile!
            </p>
            <button 
              className="btn-action btn-reset"
              onClick={handleReset}
            >
              🗑️ Reset Completo
            </button>
          </div>

          {/* Messaggio Status */}
          {message && (
            <div className={`status-message ${message.includes('✅') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
